'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface ServiceDetailProps {
  language: 'en' | 'es';
}

interface Service {
  id: string;
  name: string;
  description: string;
  subServices: string[];
}

const ServiceDetail = ({ language }: ServiceDetailProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('service-detail');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const services: { en: Service[]; es: Service[] } = {
    en: [
      {
        id: 'service_immigration',
        name: 'Immigration',
        description: 'Comprehensive immigration services for individuals and businesses navigating Canadian immigration law. We handle everything from investor visas to family reunification.',
        subServices: [
          'Investor Visas',
          'Permanent Residency',
          'Citizenship',
          'Work Visas',
          'Family Reunification',
        ],
      },
      {
        id: 'service_estate',
        name: 'Estate Planning',
        description: 'Strategic estate planning to protect your assets and ensure your wishes are carried out. We create comprehensive plans tailored to your unique situation.',
        subServices: [
          'Wills',
          'Trusts',
          'Powers of Attorney',
          'Succession Planning',
          'Asset Protection',
        ],
      },
    ],
    es: [
      {
        id: 'service_immigration',
        name: 'Inmigración',
        description: 'Servicios integrales de inmigración para individuos y empresas que navegan la ley de inmigración canadiense. Manejamos todo, desde visas de inversionista hasta reunificación familiar.',
        subServices: [
          'Visas de Inversionista',
          'Residencia Permanente',
          'Ciudadanía',
          'Visas de Trabajo',
          'Reunificación Familiar',
        ],
      },
      {
        id: 'service_estate',
        name: 'Planificación Patrimonial',
        description: 'Planificación patrimonial estratégica para proteger tus activos y asegurar que se cumplan tus deseos. Creamos planes integrales adaptados a tu situación única.',
        subServices: [
          'Testamentos',
          'Fideicomisos',
          'Poderes Legales',
          'Planificación Sucesoria',
          'Protección de Activos',
        ],
      },
    ],
  };

  const inquireText = {
    en: 'Inquire',
    es: 'Consultar',
  };

  return (
    <section id="service-detail" className="py-20 px-6">
      <div className="max-w-screen-xl mx-auto space-y-16">
        {services[language].map((service, index) => (
          <div
            key={service.id}
            className={`${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <h2 className="text-4xl font-bold text-foreground mb-4">
              {service.name}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-3xl">
              {service.description}
            </p>
            <ul className="space-y-2 mb-6">
              {service.subServices.map((subService, subIndex) => (
                <li key={`${service.id}_sub_${subIndex}`} className="flex items-center gap-2 text-foreground">
                  <span className="w-1.5 h-1.5 bg-primary sharp" />
                  <span className="text-base">{subService}</span>
                </li>
              ))}
            </ul>
            <Link
              href="/contact"
              className="inline-flex items-center text-base font-semibold text-primary hover:text-primary/80 transition-colors group"
            >
              {inquireText[language]}
              <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ServiceDetail;